import { useMemo, useState, useEffect } from 'react'

import { Box, Grid } from '@mui/material'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'

import {
  ControlledAutocomplete,
  ControlledCheckbox,
  ControlledTextField,
  IAutocompleteOptions,
  ConfirmDialog,
} from 'components/common'
import { Roles } from 'constants/index'
import {
  ReplaceBookingDriverDtoInput,
  useGetAvailableSubDriversQuery,
  useGetDriverByCurrentUserQuery,
  useGetMyVehiclesQuery,
} from 'generated/graphql'
import { useUserRole } from 'hooks'

interface IReplaceDriverFormProps {
  bookingId: number
  allocatedSubDriverIds: number[]
}

export const ReplaceDriverForm = ({
  bookingId,
  allocatedSubDriverIds,
}: IReplaceDriverFormProps) => {
  const { control, setValue } = useFormContext<ReplaceBookingDriverDtoInput>()
  const [openInactiveDriverModal, setOpenInactiveDriverModal] = useState(false)
  const [lastValidDriverId, setLastValidDriverId] = useState<any>(null)

  const { errors } = useFormState({ control })

  const selectedDriverId = useWatch({ control, name: 'driverId' })

  const { data: driverData } = useGetDriverByCurrentUserQuery()

  // A Transport Controller only allocates jobs to their drivers and never does a job themselves
  const isTransportController = useUserRole() === Roles.transportController

  const { data: vehiclesData, loading: vehiclesLoading } = useGetMyVehiclesQuery()

  const { loading: availableDriversLoading, data: availableDriversData } =
    useGetAvailableSubDriversQuery({
      variables: {
        bookingId: bookingId,
      },
      fetchPolicy: 'no-cache',
    })

  const vehicleOptions: IAutocompleteOptions = useMemo(() => {
    return vehiclesData?.myVehicles
      ? vehiclesData?.myVehicles?.map((vehicle) => ({
          label: `${vehicle.vehicleType.name} - ${vehicle.registrationNumber}`,
          value: vehicle.id,
        }))
      : []
  }, [vehiclesData?.myVehicles])

  const availableDrivers: IAutocompleteOptions = useMemo(() => {
    return availableDriversData?.availableSubDrivers
      ? availableDriversData?.availableSubDrivers
          ?.filter(
            (driver) =>
              !isTransportController || driver.id !== driverData?.driverByCurrentUser?.id,
          )
          .map((driver) => {
          const isCurrentUser = driver.id === driverData?.driverByCurrentUser?.id
          const displayName = isCurrentUser ? 'You' : driver.name
          return {
            label: driver.isActive ? displayName : `${displayName} (Blocked)`,
            value: driver.id,
            disabled: allocatedSubDriverIds.includes(Number(driver.id)),
          }
        })
      : []
  }, [
    allocatedSubDriverIds,
    availableDriversData?.availableSubDrivers,
    driverData?.driverByCurrentUser?.id,
    isTransportController,
  ])

  const getInactiveDriverName = (driverId: any) => {
    if (!driverId || !availableDriversData?.availableSubDrivers) return ''

    const selectedDriver = availableDriversData.availableSubDrivers.find(
      (driver) => driver.id === driverId,
    )

    return selectedDriver ? selectedDriver.name : ''
  }

  useEffect(() => {
    if (selectedDriverId && availableDriversData?.availableSubDrivers) {
      const selectedDriver = availableDriversData.availableSubDrivers.find(
        (driver) => driver.id === selectedDriverId,
      )

      if (selectedDriver && !selectedDriver.isActive) {
        setOpenInactiveDriverModal(true)
      } else {
        setLastValidDriverId(selectedDriverId)
      }
    }
  }, [selectedDriverId, availableDriversData?.availableSubDrivers])

  const handleInactiveDriverConfirm = () => {
    setOpenInactiveDriverModal(false)
    setValue('driverId', lastValidDriverId || '')
  }

  const handleInactiveDriverCancel = () => {
    setOpenInactiveDriverModal(false)
    setValue('driverId', lastValidDriverId || '')
  }

  return (
    <>
      <Box pt='6px'>
        <Grid container columnSpacing={3} rowSpacing={1.5}>
          <Grid item xs={12} sm={6}>
            <ControlledAutocomplete
              required
              name='driverId'
              label='Driver'
              loading={availableDriversLoading}
              options={availableDrivers}
              control={control}
              error={!!errors.driverId}
              helperText={errors.driverId?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledAutocomplete
              required
              control={control}
              name='vehicleId'
              label='Vehicle'
              loading={vehiclesLoading || availableDriversLoading}
              disabled={vehiclesLoading}
              options={vehicleOptions}
              error={!!errors.vehicleId}
              helperText={errors.vehicleId?.message}
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledCheckbox
              control={control}
              label='Show Instruction to Driver'
              name='isShownInstruction'
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              label='Instruction'
              name='instruction'
              multiline
              rows={7}
              error={!!errors.instruction}
              helperText={errors.instruction?.message}
            />
          </Grid>
        </Grid>
      </Box>

      <ConfirmDialog
        open={openInactiveDriverModal}
        onConfirm={handleInactiveDriverConfirm}
        onCancel={handleInactiveDriverCancel}
        setOpen={setOpenInactiveDriverModal}
        title='Driver Account Blocked'
        message={`The driver ${getInactiveDriverName(
          selectedDriverId,
        )} you selected is blocked due to not respecting the protocols. Please select an active driver to proceed.`}
        option1='Cancel'
        option2='Ok'
      />
    </>
  )
}
